// src/pages/QrGenerator.tsx
//
// QR generator. Pick a product, generate a signed token for it,
// and render a real, scannable QR via the `qrcode` library (SVG
// output, no canvas — works on the dark theme + SSR-safe).
//
// Per M27:
//   - Per-SKU + per-session
//   - 24h expiry
//   - Single-use (session id is unique)
//   - Signed (HMAC-SHA256)

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import { QrCode as QrIcon, Copy, RotateCw, ShieldCheck, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { DEMO_PRODUCTS } from "@/lib/demoData";
import {
  generateQRPayload,
  encodeQRPayload,
  signQRPayload,
  isQRPayloadValid,
} from "@/lib/qr";

/**
 * The shared secret for QR signing in the demo. In production this
 * would live in AWS Secrets Manager and be rotated; here we use a
 * placeholder so the demo code is self-contained.
 */
const QR_SECRET_DEMO =
  "benchmark-av-demo-qr-signing-key-v1-not-for-production";

export function QrGenerator() {
  const { t } = useTranslation();
  const [selectedSku, setSelectedSku] = useState(DEMO_PRODUCTS[0]?.sku ?? "");
  const [maxAgeHours, setMaxAgeHours] = useState(24);
  const [tick, setTick] = useState(0); // re-render trigger

  // Generate the QR when the SKU or age changes
  const { payload, encoded } = useMemo(() => {
    if (!selectedSku) return { payload: null, encoded: "" };
    const p = generateQRPayload(selectedSku);
    const signed = signQRPayload(p, QR_SECRET_DEMO);
    const enc = encodeQRPayload(signed);
    setTick((n) => n + 1);
    return { payload: signed, encoded: enc };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSku, tick === 0 ? 0 : 1]);

  const isValid = payload ? isQRPayloadValid(payload, maxAgeHours * 60 * 60 * 1000) : false;
  const expiryAt = payload ? new Date(payload.ts + maxAgeHours * 60 * 60 * 1000) : null;

  // Render the encoded payload as a real, scannable QR SVG. Caches the
  // SVG string so the layout doesn't recompute on each render. The QR
  // encodes the SAME base64url string we'd hand to the seller — the
  // "payload" card below shows the same string for copy/paste use.
  const [qrSvg, setQrSvg] = useState<string>("");
  const [qrError, setQrError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!encoded) {
      setQrSvg("");
      setQrError(null);
      return;
    }
    QRCode.toString(encoded, {
      type: "svg",
      errorCorrectionLevel: "M",
      margin: 1,
      width: 240,
      color: {
        dark: "#0f1011", // BENCHMARK surface-panel (visible on light bg)
        light: "#f7f8f8", // BENCHMARK fg-primary (high contrast)
      },
    })
      .then((svg) => {
        if (cancelled) return;
        setQrSvg(svg);
        setQrError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setQrError(err instanceof Error ? err.message : "QR render failed");
        setQrSvg("");
      });
    return () => {
      cancelled = true;
    };
  }, [encoded]);

  const copyEncoded = async () => {
    if (!encoded) return;
    try {
      await navigator.clipboard.writeText(encoded);
    } catch {
      // fallback for older browsers
      window.prompt("Copy this payload:", encoded);
    }
  };

  return (
    <div className="container py-12">
      {/* Header */}
      <header className="mb-8">
        <Badge variant="teal" size="md" className="mb-3">
          QR Generator
        </Badge>
        <h1 className="text-3xl font-510 tracking-tight text-fg-primary sm:text-4xl">
          {t("qr.title", "Generate a signed QR for any product")}
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-fg-tertiary">
          Each QR encodes a signed payload with the product SKU + a unique session id + a 24-hour expiry.
          Perfect for partner stores to verify a customer's identity at point of sale.
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* === LEFT: form === */}
        <Card>
          <CardHeader>
            <CardTitle>Generate</CardTitle>
            <CardDescription>{t("qr.pickProduct")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label htmlFor="sku" className="mb-2 block text-xs font-510 uppercase tracking-wider text-fg-tertiary">
                Product SKU
              </label>
              <select
                id="sku"
                value={selectedSku}
                onChange={(e) => setSelectedSku(e.target.value)}
                className="h-9 w-full rounded border border-border bg-bg-surface px-3 py-1.5 text-sm text-fg-primary focus:border-accent-indigo focus:outline-none"
              >
                {DEMO_PRODUCTS.map((p) => (
                  <option key={p.sku} value={p.sku}>
                    {p.name} ({p.sku})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="hours" className="mb-2 block text-xs font-510 uppercase tracking-wider text-fg-tertiary">
                Expiry (hours): {maxAgeHours}
              </label>
              <Input
                id="hours"
                type="number"
                min={1}
                max={168}
                value={maxAgeHours}
                onChange={(e) => setMaxAgeHours(Math.max(1, Math.min(168, Number(e.target.value))))}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setTick((n) => n + 1)}>
                <RotateCw className="h-4 w-4" strokeWidth={1.5} />
                Regenerate
              </Button>
              {payload && (
                <Badge variant="success" size="sm" className="self-center">
                  <ShieldCheck className="h-3 w-3" strokeWidth={1.5} />
                  Signed
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* === RIGHT: QR preview === */}
        <Card>
          <CardHeader>
            <CardTitle>Payload</CardTitle>
            <CardDescription>
              Scan or copy this token to test the read-back flow.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Visual QR — real SVG, scannable */}
            <div
              className="flex items-center justify-center rounded-lg border border-border bg-white p-4"
              data-testid="qr-svg-container"
            >
              {qrError ? (
                <div className="text-center text-xs text-rose-600">
                  QR render failed: {qrError}
                </div>
              ) : qrSvg ? (
                <div
                  className="h-60 w-60 [&_svg]:h-full [&_svg]:w-full"
                  // The SVG produced by the `qrcode` library is generated
                  // from a trusted string we own (HMAC-signed payload).
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: qrSvg }}
                />
              ) : (
                <div className="grid h-60 w-60 place-items-center text-fg-quaternary">
                  <QrIcon className="h-12 w-12" strokeWidth={1} />
                </div>
              )}
            </div>
            <p className="text-center text-[10px] uppercase tracking-wider text-fg-quaternary">
              Scan with any phone camera — encodes a signed token
            </p>

            {/* Metadata */}
            {payload && (
              <div className="space-y-2 rounded-md border border-border bg-bg-canvas p-3 text-xs">
                <div className="flex justify-between">
                  <span className="text-fg-tertiary">SKU</span>
                  <span className="font-mono text-fg-primary">{payload.sku}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-fg-tertiary">Session</span>
                  <span className="font-mono text-fg-primary">{payload.sid}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-fg-tertiary">Generated</span>
                  <span className="text-fg-primary">
                    {new Date(payload.ts).toLocaleTimeString()}
                  </span>
                </div>
                {expiryAt && (
                  <div className="flex justify-between">
                    <span className="text-fg-tertiary">Expires</span>
                    <span className="text-fg-primary">
                      {expiryAt.toLocaleTimeString()}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-fg-tertiary">Status</span>
                  <Badge variant={isValid ? "success" : "danger"} size="sm">
                    {isValid ? "Valid" : "Expired"}
                  </Badge>
                </div>
              </div>
            )}

            {/* Encoded value */}
            {encoded && (
              <div>
                <label className="mb-1.5 block text-xs font-510 uppercase tracking-wider text-fg-tertiary">
                  Encoded payload
                </label>
                <code className="block break-all rounded-md border border-border bg-bg-canvas p-3 font-mono text-xs text-fg-secondary">
                  {encoded}
                </code>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-2 w-full"
                  onClick={copyEncoded}
                >
                  <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Copy to clipboard
                </Button>
              </div>
            )}

            <div className="rounded-md border border-border bg-bg-elevated p-3 text-[11px] leading-relaxed text-fg-quaternary">
              <div className="mb-1 flex items-center gap-1.5 text-fg-secondary">
                <Clock className="h-3 w-3" strokeWidth={1.5} />
                Read-back flow
              </div>
              When the seller scans the QR:
              <br />1. Decode the base64url payload ·{" "}
              2. Verify the HMAC signature ·{" "}
              3. Check 24h expiry ·{" "}
              4. Mark the session id as consumed
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
